import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { superAdminAPI } from '../../services/api';
import TicketAttachments from '../../components/Support/TicketAttachments';
import InlineAttachments from '../../components/Support/InlineAttachments';
import ReassignmentRequestBell from '../../components/SuperAdmin/ReassignmentRequestBell';
import { useSuperAdmin } from '../../contexts/SuperAdminContext';
import { ExportMenu, DateRangeFilter, FilterSelect, ResetFiltersButton } from '../../components/SuperAdmin/ListTools';
import { fetchAllPages, dateRangeParams, formatExportDate } from '../../utils/exportData';
import toast from 'react-hot-toast';
import {
  HiOutlineTicket, HiOutlineSearch, HiOutlineX,
  HiOutlineOfficeBuilding, HiOutlinePaperAirplane, HiOutlineRefresh,
  HiOutlineExclamationCircle, HiOutlineCheckCircle, HiOutlineClock,
  HiOutlineChevronRight, HiOutlineTag, HiOutlineUserCircle, HiOutlineChatAlt2,
  HiOutlinePaperClip, HiOutlineExclamation
} from 'react-icons/hi';

// ─── Helpers ────────────────────────────────────────────────────────────────

const PRIORITY_STYLES = {
  low: 'bg-gray-100 text-gray-700',
  medium: 'bg-blue-100 text-blue-700',
  high: 'bg-amber-100 text-amber-700',
  urgent: 'bg-red-100 text-red-700',
};

const formatDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const initials = (name) =>
  (name || '?')
    .split(' ')
    .map((p) => p.charAt(0))
    .slice(0, 2)
    .join('')
    .toUpperCase();

// ─── Ticket Detail Drawer ───────────────────────────────────────────────────

const TicketDetailDrawer = ({ ticketId, onClose }) => {
  const queryClient = useQueryClient();
  const [newStageId, setNewStageId] = useState('');
  const [newPriority, setNewPriority] = useState('');
  const [newAssigneeIds, setNewAssigneeIds] = useState([]);
  const [stageReason, setStageReason] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['superadmin-ticket', ticketId],
    queryFn: async () => (await superAdminAPI.getSupportTicket(ticketId)).data,
    enabled: !!ticketId,
  });

  // Active 'support'-role admins for the assignee dropdown. Uses the
  // dedicated manage_support-gated endpoint (not GET /admins, which needs
  // manage_users) so a support-role admin managing this ticket can still
  // see/reassign to their own teammates.
  const adminsQuery = useQuery({
    queryKey: ['superadmin-assignable-admins'],
    queryFn: async () => (await superAdminAPI.getAssignableSupportAdmins()).data,
  });
  const supportAdmins = adminsQuery.data?.data || [];

  const ticket = data?.data?.ticket;
  const stages = data?.data?.stages || [];
  const pendingReassignmentRequest = data?.data?.pendingReassignmentRequest;

  React.useEffect(() => {
    if (ticket) {
      setNewStageId(ticket.stage?._id || '');
      setNewPriority(ticket.priority || 'medium');
      setNewAssigneeIds((ticket.assignedSuperAdminIds || []).map((a) => a._id));
    }
  }, [ticket?._id]);

  const toggleAssignee = (adminId) => {
    setNewAssigneeIds((prev) =>
      prev.includes(adminId) ? prev.filter((id) => id !== adminId) : [...prev, adminId]
    );
  };

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['superadmin-ticket', ticketId] });
    queryClient.invalidateQueries({ queryKey: ['superadmin-tickets'] });
    queryClient.invalidateQueries({ queryKey: ['superadmin-ticket-stats'] });
    queryClient.invalidateQueries({ queryKey: ['reassignment-requests'] });
    queryClient.invalidateQueries({ queryKey: ['reassignment-request-summary'] });
  };

  const updateMutation = useMutation({
    mutationFn: (payload) => superAdminAPI.updateSupportTicket(ticketId, payload),
    onSuccess: (res) => {
      toast.success(res.data?.message || 'Ticket updated');
      setStageReason('');
      invalidate();
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to update ticket'),
  });

  const handleUpdate = () => {
    const payload = {};
    if (ticket && String(newStageId) !== String(ticket.stage?._id || '')) payload.stageId = newStageId;
    if (newPriority !== ticket.priority) payload.priority = newPriority;
    if (payload.stageId && stageReason.trim()) payload.reason = stageReason.trim();
    const currentAssigneeIds = (ticket?.assignedSuperAdminIds || []).map((a) => a._id).sort();
    const nextAssigneeIds = [...newAssigneeIds].sort();
    const assigneesChanged =
      currentAssigneeIds.length !== nextAssigneeIds.length ||
      currentAssigneeIds.some((id, i) => id !== nextAssigneeIds[i]);
    if (assigneesChanged) payload.assigneeIds = newAssigneeIds;
    if (Object.keys(payload).length === 0) {
      toast.error('Change the status, priority or assignee first');
      return;
    }
    updateMutation.mutate(payload);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      {/* Centered modal */}
      <div
        className="w-full max-w-3xl bg-gray-50 max-h-[90vh] flex flex-col rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {isLoading || !ticket ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-500" />
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="bg-gray-900 text-white px-5 py-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-mono bg-primary-500/20 text-primary-300 px-2 py-0.5 rounded">
                      {ticket.ticketNumber}
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded font-medium ${PRIORITY_STYLES[ticket.priority] || PRIORITY_STYLES.medium}`}>
                      {ticket.priority}
                    </span>
                    {ticket.stage && (
                      <span
                        className="text-xs px-2 py-0.5 rounded font-medium text-white"
                        style={{ backgroundColor: ticket.stage.color || '#6B7280' }}
                      >
                        {ticket.stage.name}
                      </span>
                    )}
                  </div>
                  <h3 className="text-lg font-semibold mt-2 truncate">{ticket.subject}</h3>
                  <div className="flex items-center gap-1.5 text-sm text-gray-300 mt-1">
                    <HiOutlineOfficeBuilding className="w-4 h-4 flex-shrink-0" />
                    <span className="truncate">{ticket.organizationId?.name || 'Unknown Organization'}</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-gray-400 mt-1">
                    <HiOutlineUserCircle className="w-3.5 h-3.5 flex-shrink-0" />
                    {ticket.assignedSuperAdminIds?.length > 0 ? (
                      <span className="truncate">
                        Assigned to <span className="text-gray-200 font-medium">{ticket.assignedSuperAdminIds.map((a) => a.name).join(', ')}</span>
                      </span>
                    ) : (
                      <span>Unassigned</span>
                    )}
                  </div>
                </div>
                <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-700 transition-colors flex-shrink-0">
                  <HiOutlineX className="w-5 h-5 text-gray-300" />
                </button>
              </div>
            </div>

            {/* Ticket description */}
            <div className="bg-white border-b border-gray-200 px-5 py-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-7 h-7 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-xs font-semibold flex-shrink-0">
                  {initials(ticket.customerName)}
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">{ticket.customerName || 'Customer'}</p>
                  <p className="text-xs text-gray-500">
                    {ticket.customerEmail || '—'}{ticket.customerPhone ? ` • ${ticket.customerPhone}` : ''}
                  </p>
                </div>
                <span className="ml-auto text-xs text-gray-400">{formatDate(ticket.createdAt)}</span>
              </div>
              <p className="text-sm text-gray-700 whitespace-pre-wrap ml-9">{ticket.description}</p>
              {ticket.category && (
                <span className="inline-flex items-center gap-1 ml-9 mt-2 text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                  <HiOutlineTag className="w-3.5 h-3.5" />
                  {ticket.category.name}
                </span>
              )}
            </div>

            {/* Legacy ticket-level attachments (pre-migration files only —
                new uploads go on their own message in the Conversations modal) */}
            {ticket.attachments?.length > 0 && (
              <div className="px-5 py-3 bg-white border-b border-gray-200">
                <TicketAttachments attachments={ticket.attachments} />
              </div>
            )}

            {/* Actions panel — conversation viewing/replying lives in the separate
                "Conversations" CTA on the ticket list (TicketConversationModal
                below); this drawer is purely for ticket management. */}
            <div className="bg-white px-5 py-4 space-y-3">
              {pendingReassignmentRequest && (
                <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5 text-xs text-amber-800">
                  <HiOutlineExclamation className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>
                    <strong>
                      {pendingReassignmentRequest.changeSummary ||
                        `Change assignees to ${pendingReassignmentRequest.requestedAssigneeNames.join(', ') || 'Unassigned'}`}
                    </strong>{' '}
                    is pending Super Admin approval (requested by {pendingReassignmentRequest.requestedByName}).
                  </span>
                </div>
              )}
              {/* Update status / priority */}
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-2 items-end">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
                  <select
                    value={newStageId}
                    onChange={(e) => setNewStageId(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-2.5 py-2 text-sm"
                  >
                    {stages.map((s) => (
                      <option key={s._id} value={s._id}>{s.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Priority</label>
                  <select
                    value={newPriority}
                    onChange={(e) => setNewPriority(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-2.5 py-2 text-sm"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
                <button
                  onClick={handleUpdate}
                  disabled={updateMutation.isPending}
                  className="w-full bg-gray-900 text-white text-sm font-medium px-3 py-2 rounded-lg hover:bg-gray-800 disabled:opacity-50 transition-colors"
                >
                  {updateMutation.isPending ? 'Saving…' : 'Update'}
                </button>
              </div>

              {/* Assignees — currently selected shown as removable chips, with
                  separate "Change to" (replace everyone) and "Add" (append
                  one more) dropdowns instead of a checkbox list */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Assignees
                  {supportAdmins.length === 0 && !adminsQuery.isLoading && (
                    <span className="ml-1 text-amber-600 normal-case font-normal">
                      (no active support person)
                    </span>
                  )}
                </label>
                {newAssigneeIds.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {newAssigneeIds.map((id) => {
                      const admin = supportAdmins.find((a) => a._id === id);
                      return (
                        <span key={id} className="inline-flex items-center gap-1 text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-lg">
                          {admin?.name || 'Unknown'}
                          <button type="button" onClick={() => toggleAssignee(id)} className="text-gray-400 hover:text-red-500">
                            <HiOutlineX className="w-3 h-3" />
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}
                <div className="grid grid-cols-2 gap-2">
                  <select
                    value=""
                    onChange={(e) => { if (e.target.value) setNewAssigneeIds([e.target.value]); }}
                    disabled={supportAdmins.length === 0}
                    className="w-full border border-gray-300 rounded-lg px-2.5 py-2 text-sm disabled:opacity-50"
                  >
                    <option value="">Change to…</option>
                    {supportAdmins.map((a) => (
                      <option key={a._id} value={a._id}>{a.name}</option>
                    ))}
                  </select>
                  <select
                    value=""
                    onChange={(e) => { if (e.target.value) toggleAssignee(e.target.value); }}
                    disabled={supportAdmins.length === 0}
                    className="w-full border border-gray-300 rounded-lg px-2.5 py-2 text-sm disabled:opacity-50"
                  >
                    <option value="">Add…</option>
                    {supportAdmins
                      .filter((a) => !newAssigneeIds.includes(a._id))
                      .map((a) => (
                        <option key={a._id} value={a._id}>{a.name}</option>
                      ))}
                  </select>
                </div>
              </div>

              {/* Reason shown when a status change is pending — recorded on
                  the ticket and used as the reopen reason in reports */}
              {ticket && String(newStageId) !== String(ticket.stage?._id || '') && (
                <input
                  type="text"
                  value={stageReason}
                  onChange={(e) => setStageReason(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  placeholder="Reason for the status change (optional)…"
                />
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ─── Conversation-only Modal ────────────────────────────────────────────────
// A focused view of just the message thread — reachable via the "Conversations"
// CTA on each ticket row, without opening the full detail drawer (attachments,
// status/priority/assignee management). Still lets you send a quick follow-up.

const TicketConversationModal = ({ ticketId, onClose }) => {
  const queryClient = useQueryClient();
  const [followup, setFollowup] = useState('');
  const [internalNote, setInternalNote] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const fileInputRef = React.useRef(null);

  const { data, isLoading } = useQuery({
    queryKey: ['superadmin-ticket', ticketId],
    queryFn: async () => (await superAdminAPI.getSupportTicket(ticketId)).data,
    enabled: !!ticketId,
  });

  const ticket = data?.data?.ticket;

  const followupMutation = useMutation({
    mutationFn: (payload) => {
      if (pendingFiles.length === 0) return superAdminAPI.followupSupportTicket(ticketId, payload);
      const formData = new FormData();
      formData.append('message', payload.message);
      formData.append('visibleToCustomer', String(payload.visibleToCustomer));
      pendingFiles.forEach((f) => formData.append('files', f));
      return superAdminAPI.followupSupportTicketWithFiles(ticketId, formData);
    },
    onSuccess: (res) => {
      toast.success(res.data?.message || 'Follow-up sent');
      setFollowup('');
      setInternalNote(false);
      setPendingFiles([]);
      if (fileInputRef.current) fileInputRef.current.value = '';
      queryClient.invalidateQueries({ queryKey: ['superadmin-ticket', ticketId] });
      queryClient.invalidateQueries({ queryKey: ['superadmin-tickets'] });
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to send follow-up'),
  });

  const handleFollowup = (e) => {
    e.preventDefault();
    if (!followup.trim()) {
      toast.error('Write a message first');
      return;
    }
    followupMutation.mutate({ message: followup.trim(), visibleToCustomer: !internalNote });
  };

  const handlePickFiles = (fileList) => {
    const files = Array.from(fileList || []);
    const tooBig = files.find((f) => f.size > 50 * 1024 * 1024);
    if (tooBig) {
      toast.error(`"${tooBig.name}" is larger than 50MB`);
      return;
    }
    setPendingFiles((prev) => [...prev, ...files]);
  };

  const removePendingFile = (index) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl bg-gray-50 max-h-[85vh] flex flex-col rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {isLoading || !ticket ? (
          <div className="flex-1 flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-500" />
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="bg-gray-900 text-white px-5 py-4 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <HiOutlineChatAlt2 className="w-4 h-4 text-primary-300 flex-shrink-0" />
                  <span className="text-xs font-mono bg-primary-500/20 text-primary-300 px-2 py-0.5 rounded">
                    {ticket.ticketNumber}
                  </span>
                </div>
                <h3 className="text-lg font-semibold mt-2 truncate">{ticket.subject}</h3>
                <div className="flex items-center gap-1.5 text-sm text-gray-300 mt-1">
                  <HiOutlineOfficeBuilding className="w-4 h-4 flex-shrink-0" />
                  <span className="truncate">{ticket.organizationId?.name || 'Unknown Organization'}</span>
                </div>
              </div>
              <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-700 transition-colors flex-shrink-0">
                <HiOutlineX className="w-5 h-5 text-gray-300" />
              </button>
            </div>

            {/* Conversation */}
            <div className="flex-1 overflow-y-auto min-h-0 px-5 py-4 space-y-3">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Conversation ({ticket.activities?.length || 0})
              </p>
              {(ticket.activities || []).map((a, i) => {
                const isSuperAdmin = a.meta === 'superadmin';
                const isInternal = !a.visibleToCustomer;
                return (
                  <div
                    key={i}
                    className={`rounded-xl px-4 py-3 border ${
                      isSuperAdmin
                        ? 'bg-primary-50 border-primary-200'
                        : isInternal
                          ? 'bg-amber-50 border-amber-200'
                          : 'bg-white border-gray-200'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="text-xs font-semibold text-gray-800">
                        {a.createdByName || 'Unknown'}
                      </span>
                      <div className="flex items-center gap-2">
                        {isSuperAdmin && (
                          <span className="text-[10px] bg-primary-100 text-primary-700 px-1.5 py-0.5 rounded font-medium">
                            Super Admin
                          </span>
                        )}
                        {isInternal && (
                          <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium">
                            Internal
                          </span>
                        )}
                        <span className="text-xs text-gray-400">{formatDate(a.createdAt)}</span>
                      </div>
                    </div>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{a.message}</p>
                    <InlineAttachments attachments={a.attachments} />
                  </div>
                );
              })}
              {(!ticket.activities || ticket.activities.length === 0) && (
                <p className="text-sm text-gray-400 text-center py-6">No updates yet</p>
              )}
            </div>

            {/* Follow-up composer */}
            <form onSubmit={handleFollowup} className="border-t border-gray-200 bg-white px-5 py-4 space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-xs font-medium text-gray-600">Send follow-up</label>
                <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={internalNote}
                    onChange={(e) => setInternalNote(e.target.checked)}
                    className="rounded border-gray-300"
                  />
                  Internal note (hidden from org admins' customer view)
                </label>
              </div>
              {pendingFiles.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {pendingFiles.map((f, i) => (
                    <span key={i} className="inline-flex items-center gap-1 text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-lg">
                      {f.name}
                      <button type="button" onClick={() => removePendingFile(i)} className="text-gray-400 hover:text-red-500">
                        <HiOutlineX className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <div className="flex gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={(e) => handlePickFiles(e.target.files)}
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="self-stretch px-3 rounded-lg border border-gray-300 text-gray-500 hover:bg-gray-50 transition-colors"
                  title="Attach files"
                >
                  <HiOutlinePaperClip className="w-4 h-4" />
                </button>
                <textarea
                  value={followup}
                  onChange={(e) => setFollowup(e.target.value)}
                  rows={2}
                  className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none"
                  placeholder="Write a follow-up message for this ticket…"
                />
                <button
                  type="submit"
                  disabled={followupMutation.isPending || !followup.trim()}
                  className="self-stretch bg-primary-500 text-white px-4 rounded-lg hover:bg-primary-600 disabled:opacity-50 transition-colors flex items-center gap-2"
                >
                  <HiOutlinePaperAirplane className="w-4 h-4" />
                  <span className="text-sm font-medium">
                    {followupMutation.isPending ? 'Sending…' : 'Send'}
                  </span>
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

// ─── Main Page ──────────────────────────────────────────────────────────────

const EMPTY_TICKET_FILTERS = {
  search: '', priority: '', state: '', sla: '', assignee: '', organizationId: '', from: '', to: '',
};

const TICKET_EXPORT_COLUMNS = [
  { header: 'Ticket #', value: (t) => t.ticketNumber },
  { header: 'Subject', value: (t) => t.subject },
  { header: 'Organization', value: (t) => t.organizationId?.name },
  { header: 'Customer Name', value: (t) => t.customerName },
  { header: 'Customer Email', value: (t) => t.customerEmail },
  { header: 'Customer Phone', value: (t) => t.customerPhone },
  { header: 'Category', value: (t) => t.category?.name },
  { header: 'Priority', value: (t) => t.priority },
  { header: 'Stage', value: (t) => t.stage?.name },
  { header: 'State', value: (t) => (t.closedAt ? 'Closed' : t.resolvedAt ? 'Resolved' : 'Open') },
  { header: 'Assigned Support', value: (t) => (t.assignedSuperAdminIds || []).map((a) => a?.name) },
  { header: 'Assigned User', value: (t) => [t.assignedTo?.firstName, t.assignedTo?.lastName].filter(Boolean).join(' ') },
  { header: 'Response Due', value: (t) => formatExportDate(t.responseDueAt) },
  { header: 'First Response', value: (t) => formatExportDate(t.firstResponseAt) },
  { header: 'Resolution Due', value: (t) => formatExportDate(t.resolutionDueAt) },
  { header: 'Overdue', value: (t) => !t.resolvedAt && !t.closedAt && !!t.resolutionDueAt && new Date(t.resolutionDueAt) < new Date() },
  { header: 'Resolved', value: (t) => formatExportDate(t.resolvedAt) },
  { header: 'Closed', value: (t) => formatExportDate(t.closedAt) },
  { header: 'Created', value: (t) => formatExportDate(t.createdAt) },
];

const ticketApiParams = ({ from, to, ...rest }) => ({ ...rest, ...dateRangeParams(from, to) });

const SupportTickets = () => {
  const { hasPermission } = useSuperAdmin();
  const [filters, setFilters] = useState(EMPTY_TICKET_FILTERS);
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [conversationTicketId, setConversationTicketId] = useState(null);
  const setFilter = (patch) => { setFilters((prev) => ({ ...prev, ...patch })); setPage(1); };
  const filtersActive = Object.keys(EMPTY_TICKET_FILTERS).some((k) => filters[k]);

  const statsQuery = useQuery({
    queryKey: ['superadmin-ticket-stats'],
    queryFn: async () => (await superAdminAPI.getSupportTicketStats()).data?.data || {},
  });
  const stats = statsQuery.data || {};

  const ticketsQuery = useQuery({
    queryKey: ['superadmin-tickets', filters, page, limit],
    queryFn: async () =>
      (await superAdminAPI.getSupportTickets({ ...ticketApiParams(filters), page, limit })).data,
  });

  // Filter dropdown options (best-effort: a missing permission just leaves the list empty)
  const assigneesQuery = useQuery({
    queryKey: ['superadmin-assignable-admins'],
    queryFn: async () => (await superAdminAPI.getAssignableSupportAdmins()).data?.data || [],
    retry: false,
  });
  const orgsQuery = useQuery({
    queryKey: ['superadmin-org-options'],
    queryFn: async () => (await superAdminAPI.getOrganizations({ limit: 500 })).data?.data || [],
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  const tickets = ticketsQuery.data?.data || [];
  const pagination = ticketsQuery.data?.pagination || {};

  const handleSearch = (e) => {
    e.preventDefault();
    setFilter({ search: searchInput.trim() });
  };

  const statCards = [
    { label: 'Total Tickets', value: stats.total, icon: HiOutlineTicket, color: 'text-primary-600 bg-primary-50' },
    { label: 'Open', value: stats.open, icon: HiOutlineClock, color: 'text-blue-600 bg-blue-50' },
    { label: 'Urgent', value: stats.urgent, icon: HiOutlineExclamationCircle, color: 'text-red-600 bg-red-50' },
    { label: 'Resolved', value: stats.resolved, icon: HiOutlineCheckCircle, color: 'text-green-600 bg-green-50' },
  ];

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Support</h1>
          <p className="text-sm text-gray-500 mt-1">
            All support tickets raised by organizations — follow up and update them
          </p>
        </div>
        <div className="flex items-center gap-2">
          {hasPermission('manage_support') && <ReassignmentRequestBell />}
          <button
            onClick={() => {
              statsQuery.refetch();
              ticketsQuery.refetch();
            }}
            className="flex items-center gap-2 bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
          >
            <HiOutlineRefresh className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Error banner — failed requests show zeros everywhere otherwise */}
      {(statsQuery.isError || ticketsQuery.isError) && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-center justify-between">
          <p className="text-sm text-red-700">
            Failed to load tickets — please check the connection and try again.
          </p>
          <button
            onClick={() => {
              statsQuery.refetch();
              ticketsQuery.refetch();
            }}
            className="ml-4 px-3 py-1.5 rounded-lg bg-red-100 text-red-700 text-sm font-medium hover:bg-red-200 flex-shrink-0"
          >
            Retry
          </button>
        </div>
      )}

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {statCards.map((card) => {
          const Icon = card.icon;
          return (
            <div key={card.label} className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">{card.label}</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    {statsQuery.isLoading ? '…' : card.value ?? 0}
                  </p>
                </div>
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${card.color}`}>
                  <Icon className="w-5 h-5" />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <form onSubmit={handleSearch} className="relative flex-1">
            <HiOutlineSearch className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm"
              placeholder="Search by ticket #, subject, customer name, email or phone… (press Enter)"
            />
          </form>
          <ExportMenu
            fileName="support-tickets"
            columns={TICKET_EXPORT_COLUMNS}
            getRows={() => fetchAllPages(superAdminAPI.getSupportTickets, ticketApiParams(filters))}
          />
        </div>
        <div className="flex flex-wrap items-center gap-3 mt-3">
          <FilterSelect
            value={filters.priority}
            onChange={(v) => setFilter({ priority: v })}
            allLabel="All Priorities"
            options={[['low', 'Low'], ['medium', 'Medium'], ['high', 'High'], ['urgent', 'Urgent']]}
          />
          <FilterSelect
            value={filters.state}
            onChange={(v) => setFilter({ state: v })}
            allLabel="All States"
            options={[['open', 'Open'], ['resolved', 'Resolved'], ['closed', 'Closed']]}
          />
          <FilterSelect
            value={filters.sla}
            onChange={(v) => setFilter({ sla: v })}
            allLabel="Any SLA"
            options={[['overdue', 'Overdue'], ['on_track', 'On track']]}
          />
          <FilterSelect
            value={filters.assignee}
            onChange={(v) => setFilter({ assignee: v })}
            allLabel="All Assignees"
            options={[['unassigned', 'Unassigned'], ...(assigneesQuery.data || []).map((a) => [a._id, a.name])]}
          />
          {(orgsQuery.data || []).length > 0 && (
            <FilterSelect
              value={filters.organizationId}
              onChange={(v) => setFilter({ organizationId: v })}
              allLabel="All Organizations"
              options={orgsQuery.data.map((o) => [o._id, o.name])}
              className="max-w-[220px]"
            />
          )}
          <DateRangeFilter
            label="Created"
            from={filters.from}
            to={filters.to}
            onChange={({ from, to }) => setFilter({ from, to })}
          />
          <ResetFiltersButton
            active={filtersActive}
            onReset={() => { setSearchInput(''); setFilter(EMPTY_TICKET_FILTERS); }}
          />
        </div>
      </div>

      {/* Tickets list */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {ticketsQuery.isLoading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-500" />
          </div>
        ) : tickets.length === 0 ? (
          <div className="text-center py-16">
            <HiOutlineTicket className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">No tickets found</p>
            <p className="text-sm text-gray-400 mt-1">
              {filtersActive ? 'Try changing the filters' : 'Tickets raised by organizations will appear here'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {tickets.map((t) => (
              <button
                key={t._id}
                onClick={() => setSelectedTicketId(t._id)}
                className="w-full text-left px-4 py-3.5 hover:bg-gray-50 transition-colors flex items-center gap-4"
              >
                <div className="w-9 h-9 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-xs font-semibold flex-shrink-0">
                  {initials(t.customerName || t.organizationId?.name)}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-mono text-gray-500">{t.ticketNumber}</span>
                    <span className="text-sm font-medium text-gray-900 truncate">{t.subject}</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-gray-500 mt-0.5">
                    <HiOutlineOfficeBuilding className="w-3.5 h-3.5 flex-shrink-0" />
                    <span className="truncate">{t.organizationId?.name || '—'}</span>
                    <span className="text-gray-300">•</span>
                    <span className="whitespace-nowrap">{formatDate(t.createdAt)}</span>
                    {t.assignedSuperAdminIds?.length > 0 && (
                      <>
                        <span className="text-gray-300">•</span>
                        <span className="truncate" title="Assigned support staff">
                          {t.assignedSuperAdminIds.map((a) => a.name).join(', ')}
                        </span>
                      </>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className={`text-xs px-2 py-1 rounded font-medium capitalize ${PRIORITY_STYLES[t.priority] || PRIORITY_STYLES.medium}`}>
                    {t.priority}
                  </span>
                  {t.stage && (
                    <span
                      className="text-xs px-2 py-1 rounded font-medium text-white"
                      style={{ backgroundColor: t.stage.color || '#6B7280' }}
                    >
                      {t.stage.name}
                    </span>
                  )}
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => { e.stopPropagation(); setConversationTicketId(t._id); }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        e.stopPropagation();
                        setConversationTicketId(t._id);
                      }
                    }}
                    title="View conversation"
                    className="flex items-center gap-1.5 text-xs font-medium text-primary-600 bg-primary-50 hover:bg-primary-100 px-2.5 py-1.5 rounded-lg transition-colors"
                  >
                    <HiOutlineChatAlt2 className="w-3.5 h-3.5" />
                    Conversations
                  </span>
                  <HiOutlineChevronRight className="w-4 h-4 text-gray-300" />
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Pagination */}
        {pagination.total > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
            <div className="flex items-center gap-4">
              <p className="text-sm text-gray-500">
                Page {pagination.page} of {pagination.totalPages || 1} • {pagination.total} tickets
              </p>
              <label className="flex items-center gap-2 text-sm text-gray-500 whitespace-nowrap">
                Rows per page:
                <select
                  value={limit}
                  onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
                  className="w-auto px-2 py-1 text-sm border border-gray-300 rounded-lg bg-white"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </label>
            </div>
            {pagination.totalPages > 1 && (
              <div className="flex gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={pagination.page <= 1}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg bg-white disabled:opacity-40 hover:bg-gray-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                  disabled={pagination.page >= pagination.totalPages}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg bg-white disabled:opacity-40 hover:bg-gray-50"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Detail drawer */}
      {selectedTicketId && (
        <TicketDetailDrawer
          ticketId={selectedTicketId}
          onClose={() => setSelectedTicketId(null)}
        />
      )}

      {/* Conversation-only modal — opened via the "Conversations" CTA on a row */}
      {conversationTicketId && (
        <TicketConversationModal
          ticketId={conversationTicketId}
          onClose={() => setConversationTicketId(null)}
        />
      )}
    </div>
  );
};

export default SupportTickets;